<template>
  <div class="gift-with-purchase" />
</template>

<script>

/**
 * Vue: Gift with purchase (gift-with-purchase)
 * -----------------------------------------------------------------------------
 * Used to set gift with purchase settings and trigger modal.
 * - No styles required.
 *
 * @param {Array} blocks - Section blocks.
 * @param {Object} settings - Gift with purchase section settings.
 * @param {String} currentMarketHandle - Current market handle.
 *
 */
/* eslint-disable no-underscore-dangle */
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'GiftWithPurchase',

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({
        enable: false,
        excludedProductTypes: [],
        minimumPrice: 0,
        priceRounding: true,
      }),
    },
    currentMarketHandle: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      global: true,
      removingItems: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getGiftIdDismissed: 'gift-with-purchase/getIdDismissed',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      availablePromotions: (state) => state['gift-with-purchase'].available,
      cartItems: (state) => state.cart.response.items,
      enablePromotions: (state) => state['gift-with-purchase'].settings.enable,
      inactivePromotions: (state) => state['gift-with-purchase'].inactive,
      promotions: (state) => state['gift-with-purchase'].promotions,
    }),
  },

  watch: {

    /**
     * Watch inactive promotions to remove products.
     */
    inactivePromotions() {
      this.removeGiftProducts()
    },
  },

  mounted() {
    this.setPromotions({
      blocks: this.blocks,
      settings: this.settings,
      currentMarketHandle: this.currentMarketHandle,
    })
    this.removeGiftProducts()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      queueOverlay: 'overlays/queue',
      setDismissed: 'gift-with-purchase/setDismissed',
      setPromotions: 'gift-with-purchase/setPromotions',
      updateItem: 'cart/updateItem',
    }),

    /**
     * Removes gift products.
     * - Go through cart items and find the key of the promotion that is in
     *   inactive or no longer exists that needs removing and build object.
     * - If gift product and promotions disabled then remove.
     */
    async removeGiftProducts() {
      if (this.removingItems) {
        return
      }

      this.removingItems = true

      const keysToRemove = this.cartItems.map((item) => {
        if (!item.properties?.__gift) {
          return false
        }

        if (!this.enablePromotions) {
          return item.key
        }

        const exists = this.promotions.find((promotion) => {
          return promotion.id === item.properties.__gift
        })

        const inactive = this.inactivePromotions.find((promotion) => {
          return promotion.id === item.properties.__gift
        })

        if (exists && !inactive) {
          return false
        }

        return item.key
      }).filter(Boolean)

      const payload = keysToRemove.map((id) => {
        return { id, quantity: 0 }
      })

      if (payload.length === 0) {
        this.removingItems = false
        return
      }

      try {
        await this.updateItem(payload)
        this.removingItems = false

      } catch (error) {
        this.removingItems = false
        throw new Error('Failed to remove inactive', error)
      }
    },
  },
}
</script>
